import React from 'react'

import { makeStyles } from '@material-ui/core/styles'
import { EllipsisTextField } from 'components/fields/ellipsisTextField'
import { Datagrid, TextField } from 'react-admin'
import { useSelector } from 'react-redux'

import { orange, green, red } from '../../constants/colorConstants'
import tracker from '../../utils/tracker'
import { EventsInvoiceExpandableView } from '../expandableViews/eventsInvoiceExpandableView'
import { CurrencyField } from '../fields/currencyField'
import { DueDateField } from '../fields/dueDateField'
import { LocalDateField } from '../fields/localDateField'
import { RemainingBalanceField } from '../fields/remainingBalanceField'
import { StatusField } from '../fields/statusField'
import { StatusSubtitleField } from '../fields/statusSubtitleField'
import { ViewCreditLineField } from '../fields/viewCreditLineField'
import { CustomDatagridBody } from './customDatagridBody'
import { CustomDatagridRow } from './customDatagridRow'

const useStyles = makeStyles((theme) => ({
  dueDate: {
    whiteSpace: 'nowrap',
  },
  status: {
    marginTop: '6px',
  },
  statusSubtitleAdvanced: {
    fontSize: '9px',
    lineHeight: '10.55px',
    whiteSpace: 'nowrap',
    ...theme.mundi.font.roboto400Normal,
  },
  statusSubtitlePaid: {
    fontSize: '13px',
    lineHeight: '15.23px',
    whiteSpace: 'nowrap',
    color: green,
    ...theme.mundi.font.roboto600Normal,
  },
  statusSubtitleDefaulted: {
    fontSize: '13px',
    lineHeight: '15.23px',
    whiteSpace: 'nowrap',
    color: red,
    ...theme.mundi.font.roboto600Normal,
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
  invoiceID: {
    whiteSpace: 'nowrap',
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '10rem',
  },
  importerName: {
    whiteSpace: 'nowrap',
    ...theme.mundi.font.roboto500Normal,
    '&:visited': {
      color: `${orange}`,
    },
    '&:link': {
      color: `${orange}`,
    },
  },
  highlight: {
    whiteSpace: 'nowrap',
    ...theme.mundi.font.roboto700Normal,
  },
  green: {
    color: green,
    ...theme.mundi.font.roboto500Normal,
  },
  red: {
    color: red,
    ...theme.mundi.font.roboto500Normal,
  },
  headerRightWithIcon: {
    textAlign: 'right',
    paddingRight: '85px',
  },
  headerRightWithoutIcon: {
    textAlign: 'right',
    paddingRight: '40px',
  },
  scrollPanelWithSidebarClosed: {
    display: 'flex',
    overflowY: 'auto',
    overflowX: 'auto',
    width: 'calc(100vw - 200px)',
    maxHeight: 'calc(95vh - 250px)',
  },
  scrollPanelWithSidebarOpened: {
    display: 'flex',
    overflowY: 'auto',
    overflowX: 'auto',
    width: 'calc(100vw - 300px)',
    maxHeight: 'calc(95vh - 250px)',
  },
  [theme.breakpoints.down('sm')]: {
    scrollPanel: {
      maxWidth: '80vw',
    },
  },
}))

export const InvoiceAction = (props) => {
  const classes = useStyles()
  return (
    <div>
      <StatusField
        source={props.record.tabName.toUpperCase()}
        className={classes.status}
        {...props}
      />
      <StatusSubtitleField
        source={props.record.tabName.toUpperCase()}
        className={classes.statusSubtitleAdvanced}
        {...props}
      />
    </div>
  )
}

export const PaidInvoiceAction = (props) => {
  const classes = useStyles()

  return (
    <StatusSubtitleField
      source={props.record.tabName.toUpperCase()}
      className={classes.statusSubtitlePaid}
      {...props}
    />
  )
}

const handleUserExpand = (props) => {
  tracker.event({
    action: 'invoice_advance_details_expanded',
    category: 'invoiceAdvance',
    label: props.record ? props.record.invoiceNumber : undefined,
  })
}

const handleUserCollapse = (props) => {
  tracker.event({
    action: 'invoice_advance_details_collapsed',
    category: 'invoiceAdvance',
    label: props.record ? props.record.invoiceNumber : undefined,
  })
}

export const AdvancedDatagrid = ({
  basePath,
  linkDataCreditLines,
  ...props
}) => {
  const classes = useStyles()
  const open = useSelector((state) => state.admin.ui.sidebarOpen)

  return (
    <div
      className={
        open
          ? classes.scrollPanelWithSidebarOpened
          : classes.scrollPanelWithSidebarClosed
      }
    >
      <Datagrid
        {...props}
        expand={
          <EventsInvoiceExpandableView
            linkDataCreditLines={linkDataCreditLines}
          />
        }
        body={
          <CustomDatagridBody
            {...props}
            row={
              <CustomDatagridRow
                {...props}
                onUserExpand={handleUserExpand}
                onUserCollapse={handleUserCollapse}
              />
            }
          />
        }
      >
        <EllipsisTextField
          source="invoiceNumber"
          label="resources.invoices.fields.invoiceId"
          className={classes.invoiceID}
        />
        <ViewCreditLineField
          source="importerName"
          sourceCreditLine="creditLine"
          label="resources.invoices.fields.importerName"
          linkDataCreditLines={linkDataCreditLines}
          className={classes.importerName}
        />
        <LocalDateField
          source="issuedDate"
          label="resources.invoices.fields.issuedDate"
        />
        <DueDateField
          source="dueDate"
          label="resources.invoices.fields.dueDate"
          className={classes.dueDate}
        />
        <CurrencyField
          source="invoiceAmount"
          label="resources.invoices.fields.invoiceAmount"
          className={classes.noWrap}
          headerClassName={classes.headerRightWithoutIcon}
        />
        <RemainingBalanceField
          source="effectiveOrAdvanceAmount"
          label="resources.invoices.fields.advanceAmount"
          className={classes.noWrap}
          headerClassName={classes.headerRightWithIcon}
        />
        <RemainingBalanceField
          source="feeAmount"
          label="resources.invoices.fields.cost"
          className={classes.noWrap}
          headerClassName={classes.headerRightWithIcon}
        />
        <RemainingBalanceField
          source="remainingBalance"
          label="resources.invoices.fields.remainingBalance"
          className={classes.highlight}
          headerClassName={classes.headerRightWithIcon}
        />
        <InvoiceAction />
      </Datagrid>
    </div>
  )
}

export const PaidDatagrid = ({ basePath, linkDataCreditLines, ...props }) => {
  const classes = useStyles()
  const open = useSelector((state) => state.admin.ui.sidebarOpen)

  return (
    <div
      className={
        open
          ? classes.scrollPanelWithSidebarOpened
          : classes.scrollPanelWithSidebarClosed
      }
    >
      <Datagrid
        {...props}
        expand={
          <EventsInvoiceExpandableView
            linkDataCreditLines={linkDataCreditLines}
          />
        }
        body={
          <CustomDatagridBody
            {...props}
            row={
              <CustomDatagridRow
                {...props}
                onUserExpand={handleUserExpand}
                onUserCollapse={handleUserCollapse}
              />
            }
          />
        }
      >
        <TextField
          source="invoiceNumber"
          label="resources.invoices.fields.invoiceId"
          className={classes.noWrap}
        />
        <ViewCreditLineField
          source="importerName"
          sourceCreditLine="creditLine"
          label="resources.invoices.fields.importerName"
          linkDataCreditLines={linkDataCreditLines}
          className={classes.importerName}
        />
        <LocalDateField
          source="issuedDate"
          label="resources.invoices.fields.issuedDate"
        />
        <DueDateField
          source="dueDate"
          label="resources.invoices.fields.dueDate"
          className={classes.dueDate}
        />
        <CurrencyField
          source="invoiceAmount"
          label="resources.invoices.fields.invoiceAmount"
          className={classes.noWrap}
          headerClassName={classes.headerRightWithoutIcon}
        />
        <CurrencyField
          source="receivedAmount"
          label="resources.invoices.fields.receivedAmount"
          className={classes.noWrap}
          headerClassName={classes.headerRightWithoutIcon}
        />
        <CurrencyField
          source="feeAmount"
          label="resources.invoices.fields.feeAmount"
          className={classes.highlight}
          headerClassName={classes.headerRightWithoutIcon}
        />
        <PaidInvoiceAction />
      </Datagrid>
    </div>
  )
}
